import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, RefObject } from 'react';
import { useRef } from 'react';
import type { ReadMoreProps } from './ReadMore.data';
import styles from './ReadMore.module.scss';
import { setupTransitionInTimeline, setupTransitionOutTimeline } from './ReadMore.transitions';
import { useChangeTransitionState } from 'util/react-transition-component/hooks/useChangeTransitionState';
import { useTransitionController } from 'util/react-transition-component';
import type { RichTextBlokData } from '../rich-text/RichText.data';
import type { StoryblokComponent } from 'storyblok-js-client';
import PrimaryButton from '../../atoms/primary-button/PrimaryButton';

import RichText from '../rich-text/RichText';
import type { CtaRefType } from '../../../util/cta/cta.data';
import { useScrollProviderContext } from '../../unlisted/ScrollProvider';
import type { ScrollProviderContextType } from '../../unlisted/ScrollProvider';
import useThemeScrollBlock from '../../../hooks/useThemeScrollBlock';

export default function ReadMore({ isVisible, className, blok, ref }: ReadMoreProps): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;

  const transitionController = useTransitionController(
    () => ({
      ref: elementRef,
      refs: {
        elementRef,
      },
      setupTransitionInTimeline,
      setupTransitionOutTimeline,
    }),
    [],
  );

  useChangeTransitionState(transitionController, isVisible);

  const richTextContainerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<CtaRefType>(null);

  const { scrollIntoView } = useScrollProviderContext() as ScrollProviderContextType;

  useThemeScrollBlock(elementRef, blok.use_accent_color);

  return (
    <div ref={elementRef} className={classNames(styles.readMore, className)} {...sbEditable(blok)}>
      <RichText
        blok={{ text: blok.preview_text } as RichTextBlokData & StoryblokComponent<string>}
        className={styles.previewText}
        ref={richTextContainerRef}
      >
        <PrimaryButton
          disableTriggerInOnScroll={false}
          ref={buttonRef}
          className={styles.button}
          label={blok.button_label}
          onClick={() => {
            const element = document?.querySelector(`[data-scroll-to=download-pdf]`);
            if (element) {
              scrollIntoView(element);
            }
          }}
        />
      </RichText>
    </div>
  );
}
